import { Loader2 } from "lucide-react";
import { useMemo } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { useAuth } from "../hooks/use-auth";
import { useLoginFormController } from "../hooks/use-login-form-controller";
import { LoginForms } from "./LoginForms";

interface LoginLocationState {
  from?: string;
  reason?: string;
}

function getSafeReturnPath(value: unknown) {
  if (typeof value !== "string") {
    return "/";
  }
  if (!value.startsWith("/")) {
    return "/";
  }
  // Browsers treat a backslash like a slash here, so /\host leaves the origin.
  if (/^[/\\]{2}/.test(value)) {
    return "/";
  }
  return value;
}

function getLoginNotice(authError: string | null, routeReason: string | undefined) {
  if (authError === "approval_pending") {
    return "Your account is pending approval. Contact an administrator.";
  }
  if (routeReason === "approval_pending") {
    return "Your account is pending approval. Contact an administrator.";
  }
  if (authError === "session_invalid") {
    return "We couldn't establish a secure session. Please try signing in again, or contact an administrator if this keeps happening.";
  }
  return null;
}

function shouldRedirectToApp({
  isApproved,
  isLoading,
  user,
}: {
  isApproved: boolean;
  isLoading: boolean;
  user: unknown;
}) {
  if (isLoading) {
    return false;
  }
  if (!user) {
    return false;
  }
  return isApproved;
}

function LoginNotice({ notice }: { notice: string | null }) {
  if (!notice) {
    return null;
  }
  return (
    <Alert className="border-primary/40 bg-primary/10" id="login-notice" role="status">
      <AlertDescription>{notice}</AlertDescription>
    </Alert>
  );
}

function LoadingSession() {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground text-sm">
      <Loader2 className="size-4 animate-spin motion-reduce:animate-none" />
      Checking session…
    </div>
  );
}

function LoginPanel({
  isLoading,
  loginForms,
}: {
  isLoading: boolean;
  loginForms: ReturnType<typeof useLoginFormController>;
}) {
  if (isLoading) {
    return <LoadingSession />;
  }
  return <LoginForms {...loginForms} />;
}

function BrandMark() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex size-9 items-center justify-center rounded-xl border border-primary/25 bg-primary/10">
        <img alt="" className="size-5 dark:hidden" src="/xan-icon-light-mode.svg" />
        <img alt="" className="hidden size-5 dark:block" src="/xan-icon-dark-mode.svg" />
      </div>
      <span className="font-display font-semibold text-foreground text-sm tracking-tight">
        Xan Finance
      </span>
    </div>
  );
}

function BrandSide() {
  return (
    <aside className="relative hidden overflow-hidden lg:flex lg:w-1/2 lg:flex-col lg:justify-between lg:border-border lg:border-r lg:bg-muted/20 lg:p-12">
      <BrandMark />

      <div className="login-rise space-y-7">
        <h1 className="max-w-lg text-balance font-display text-[2.75rem] leading-[1.02] tracking-[-0.03em] xl:text-[3.5rem]">
          Finance operations for teams that move.
        </h1>
        <p className="max-w-sm text-base text-muted-foreground leading-relaxed">
          Invoices, estimates, payments, inventory. One lean shell for the work that keeps cash
          flowing.
        </p>
      </div>

      <div className="space-y-4">
        <div className="h-px w-16 bg-border" />
        <p className="text-muted-foreground text-xs">
          Staff access only. Accounts require approval.
        </p>
      </div>
    </aside>
  );
}

/** Renders the public login screen and redirects approved staff to the requested app route. */
export function LoginPage() {
  const { authError, completeSignIn, isApproved, isLoading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const navState = location.state as LoginLocationState | null;
  const redirectTo = useMemo(() => getSafeReturnPath(navState?.from), [navState]);
  const loginForms = useLoginFormController({
    completeSignIn: completeSignIn ?? (() => false),
    isAuthLoading: isLoading,
    initialAuthMode: location.pathname === "/reset-password" ? "reset" : "password",
    navigate,
    redirectTo,
  });
  const routedLoginForms = {
    ...loginForms,
    onAuthModeChange: (mode: Parameters<typeof loginForms.onAuthModeChange>[0]) => {
      if (mode === "reset" || location.pathname === "/reset-password") {
        void navigate(mode === "reset" ? "/reset-password" : "/login", { state: navState });
      } else {
        loginForms.onAuthModeChange(mode);
      }
    },
    onResetReset: () => {
      void navigate("/login", { state: navState });
    },
  };
  const notice = useMemo(() => getLoginNotice(authError, navState?.reason), [authError, navState]);

  if (shouldRedirectToApp({ isApproved, isLoading, user })) {
    return <Navigate replace to={redirectTo} />;
  }

  return (
    <div className="flex min-h-[100dvh] w-full bg-background text-foreground selection:bg-primary selection:text-primary-foreground">
      <BrandSide />

      <main className="relative flex flex-1 items-center justify-center p-6 sm:p-10">
        {/* Mobile-only brand header */}
        <div className="absolute top-6 left-6 lg:hidden">
          <BrandMark />
        </div>

        {/*
          The entrance lives on the brand column only. This column swaps a
          session spinner for the form, so an animation here would be spent on
          the spinner and the form would pop in behind it.
        */}
        <div className="relative w-full max-w-[26rem] space-y-7">
          <h2 className="font-display font-semibold text-[1.75rem] leading-tight tracking-[-0.02em]">
            {loginForms.authMode === "reset" ? "Reset your password" : "Welcome back"}
          </h2>

          <LoginNotice notice={notice} />

          <Card className="rounded-2xl p-6 sm:p-7" variant="glow">
            <LoginPanel isLoading={isLoading} loginForms={routedLoginForms} />
          </Card>
        </div>
      </main>
    </div>
  );
}
